import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
// import VoiceAnalyticsTab from './VoiceAnalyticsTab';  // ❌ Rimosso: causava errori 401
// import VoiceChatTab from './VoiceChatTab';  // ❌ Rimosso: causava errori 401 e "Amara"
import { API_BASE_URL, getApiHeaders } from '../config/api';

/**
 * AI Training Studio - Strumento per sviluppatori
 * - Test conversazioni in tempo reale
 * - Monitoraggio costi API (tokens, modelli)
 * - Training rules (regole comportamento)
 * - Blocklist (risposte da evitare)
 * - Logs conversazioni con analisi
 * - Prompt system editor
 */
export default function AITrainingStudio() {
  // Claude.ai light theme colors
  const colors = {
    bg: '#f7f7f8',
    surface: '#ffffff',
    surfaceVariant: '#f0f0f1',
    primary: '#007AFF',
    danger: '#FF3B30',
    warning: '#FF9500',
    textPrimary: '#1a1a1a',
    textSecondary: '#666666',
    border: '#e5e5e5',
  };

  const styles = {
    container: {
      padding: '24px',
      background: colors.bg,
      minHeight: '100vh',
      color: colors.textPrimary,
      fontFamily: 'monospace',
    },
    header: {
      marginBottom: '32px',
      paddingBottom: '16px',
      borderBottom: `2px solid ${colors.primary}`,
    },
    title: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: colors.primary,
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '14px',
      color: colors.textSecondary,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '24px',
      marginBottom: '24px',
    },
    card: {
      background: colors.surface,
      padding: '20px',
      borderRadius: '8px',
      border: `1px solid ${colors.border}`,
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '16px',
      color: colors.primary,
    },
    input: {
      width: '100%',
      padding: '12px',
      background: colors.surfaceVariant,
      border: `1px solid ${colors.border}`,
      borderRadius: '4px',
      color: colors.textPrimary,
      fontFamily: 'monospace',
      fontSize: '14px',
      marginBottom: '12px',
    },
    textarea: {
      width: '100%',
      padding: '12px',
      background: colors.surfaceVariant,
      border: `1px solid ${colors.border}`,
      borderRadius: '4px',
      color: colors.textPrimary,
      fontFamily: 'monospace',
      fontSize: '14px',
      minHeight: '120px',
      resize: 'vertical',
    },
    button: {
      padding: '12px 24px',
      background: colors.primary,
      color: '#ffffff',
      border: 'none',
      borderRadius: '4px',
      fontWeight: 'bold',
      cursor: 'pointer',
      fontSize: '14px',
      fontFamily: 'monospace',
    },
    buttonDanger: {
      background: colors.danger,
      color: '#fff',
    },
    buttonSecondary: {
      background: colors.surfaceVariant,
      color: colors.textPrimary,
      border: `1px solid ${colors.border}`,
    },
    chatBox: {
      background: colors.surfaceVariant,
      padding: '16px',
      borderRadius: '8px',
      height: '400px',
      overflowY: 'auto',
      marginBottom: '12px',
      border: `1px solid ${colors.border}`,
    },
    message: {
      marginBottom: '16px',
      padding: '12px',
      borderRadius: '6px',
    },
    userMessage: {
      background: colors.primary,
      color: '#ffffff',
      marginLeft: '20%',
    },
    aiMessage: {
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      marginRight: '20%',
    },
    stat: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '12px',
      background: colors.surfaceVariant,
      borderRadius: '4px',
      marginBottom: '8px',
    },
    statLabel: {
      color: colors.textSecondary,
    },
    statValue: {
      color: colors.primary,
      fontWeight: 'bold',
    },
    badge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      marginRight: '8px',
      marginBottom: '8px',
    },
    badgeDanger: {
      background: colors.danger,
      color: '#fff',
    },
    badgeWarning: {
      background: colors.warning,
      color: '#ffffff',
    },
    codeBlock: {
      background: colors.bg,
      padding: '12px',
      borderRadius: '4px',
      border: `1px solid ${colors.border}`,
      fontSize: '12px',
      overflowX: 'auto',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
  };

  // State
  const [activeTab, setActiveTab] = useState('test'); // test, rules, blocklist, logs, system
  const [testMessage, setTestMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Training Rules
  const [rules, setRules] = useState([]);
  const [newRule, setNewRule] = useState({ trigger: '', action: '', description: '' });

  // Blocklist
  const [blocklist, setBlocklist] = useState([]);
  const [newBlock, setNewBlock] = useState({ pattern: '', reason: '' });

  // System Prompt
  const [systemPrompt, setSystemPrompt] = useState('');

  // API Stats
  const [stats, setStats] = useState({
    totalTokens: 0,
    totalCost: 0,
    conversationCount: 0,
    avgResponseTime: 0,
    model: 'gpt-4o-mini',
  });

  // Logs
  const [logs, setLogs] = useState([]);

  const chatBoxRef = useRef(null);

  useEffect(() => {
    loadTrainingData();
    loadStats();
    loadLogs();
  }, []);

  useEffect(() => {
    // Auto-scroll chat
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [chatHistory]);

  async function loadTrainingData() {
    try {
      const [rulesRes, blockRes, promptRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/training/rules`, { headers: getApiHeaders() }),
        axios.get(`${API_BASE_URL}/api/training/blocklist`, { headers: getApiHeaders() }),
        axios.get(`${API_BASE_URL}/api/training/system-prompt`, { headers: getApiHeaders() }),
      ]);

      setRules(rulesRes.data.rules || []);
      setBlocklist(blockRes.data.blocklist || []);
      setSystemPrompt(promptRes.data.prompt || '');
    } catch (error) {
      console.error('Error loading training data:', error);
    }
  }

  async function loadStats() {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/training/stats`, { headers: getApiHeaders() });
      const data = res.data;
      // Map snake_case to camelCase
      setStats({
        totalTokens: data.total_tokens || 0,
        totalCost: data.total_cost || 0,
        conversationCount: data.conversation_count || 0,
        avgResponseTime: data.avg_response_time || 0,
        model: data.model || 'gpt-4o-mini'
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }

  async function loadLogs() {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/training/logs?limit=50`, { headers: getApiHeaders() });
      const logs = res.data.logs || [];
      // Map snake_case to camelCase
      const mappedLogs = logs.map(log => ({
        id: log.id,
        assistedName: log.assisted_name,
        userMessage: log.user_message,
        aiResponse: log.ai_response,
        tokens: log.tokens,
        cost: log.cost,
        responseTime: log.response_time,
        blocked: log.blocked,
        blockedReason: log.blocked_reason,
        model: log.model,
        timestamp: log.timestamp,
        userType: log.user_type
      }));
      setLogs(mappedLogs);
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  }

  useEffect(() => {
    // Auto-refresh logs ogni 5 secondi
    const interval = setInterval(() => {
      loadLogs();
    }, 5000); // 5000ms = 5 secondi

    // Cleanup quando component unmount
    return () => clearInterval(interval);
  }, []);

  async function sendTestMessage() {
    if (!testMessage.trim()) return;

    const userMessage = { role: 'user', content: testMessage, timestamp: new Date() };
    setChatHistory([...chatHistory, userMessage]);
    setTestMessage('');
    setIsLoading(true);

    try {
      const startTime = Date.now();
      const res = await axios.post(`${API_BASE_URL}/api/training/test-chat`, {
        message: testMessage,
        history: chatHistory,
      }, { headers: getApiHeaders() });

      const responseTime = Date.now() - startTime;
      const aiMessage = {
        role: 'assistant',
        content: res.data.response,
        timestamp: new Date(),
        tokens: res.data.tokens,
        cost: res.data.cost,
        responseTime,
        blocked: res.data.blocked || false,
        blockedReason: res.data.blockedReason,
      };

      setChatHistory([...chatHistory, userMessage, aiMessage]);

      // Aggiorna stats
      loadStats();
      loadLogs();
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: `❌ Errore: ${error.message}`,
        timestamp: new Date(),
        error: true,
      };
      setChatHistory([...chatHistory, userMessage, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }

  async function saveRule() {
    if (!newRule.trigger || !newRule.action) {
      alert('Trigger e Action sono obbligatori');
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/training/rules`, newRule, { headers: getApiHeaders() });
      setNewRule({ trigger: '', action: '', description: '' });
      loadTrainingData();
      alert('✅ Regola salvata');
    } catch (error) {
      alert('❌ Errore salvataggio regola: ' + error.message);
    }
  }

  async function deleteRule(id) {
    if (!window.confirm('Eliminare questa regola?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/training/rules/${id}`, { headers: getApiHeaders() });
      loadTrainingData();
    } catch (error) {
      alert('❌ Errore eliminazione regola: ' + error.message);
    }
  }

  async function saveBlock() {
    if (!newBlock.pattern) {
      alert('Pattern è obbligatorio');
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/training/blocklist`, newBlock, { headers: getApiHeaders() });
      setNewBlock({ pattern: '', reason: '' });
      loadTrainingData();
      alert('✅ Blocco salvato');
    } catch (error) {
      alert('❌ Errore salvataggio blocco: ' + error.message);
    }
  }

  async function deleteBlock(id) {
    if (!window.confirm('Eliminare questo blocco?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/training/blocklist/${id}`, { headers: getApiHeaders() });
      loadTrainingData();
    } catch (error) {
      alert('❌ Errore eliminazione blocco: ' + error.message);
    }
  }

  async function saveSystemPrompt() {
    try {
      await axios.post(`${API_BASE_URL}/api/training/system-prompt`, { prompt: systemPrompt }, { headers: getApiHeaders() });
      alert('✅ System Prompt salvato');
    } catch (error) {
      alert('❌ Errore salvataggio prompt: ' + error.message);
    }
  }

  function clearChat() {
    if (!window.confirm('Cancellare tutta la chat?')) return;
    setChatHistory([]);
  }

  function renderTestTab() {
    return (
      <div>
        <div style={styles.grid}>
          {/* Chat Box */}
          <div style={{ ...styles.card, gridColumn: 'span 2' }}>
            <div style={styles.cardTitle}>💬 Test Conversazione</div>

            <div ref={chatBoxRef} style={styles.chatBox}>
              {chatHistory.length === 0 && (
                <div style={{ textAlign: 'center', color: colors.textSecondary, marginTop: '160px' }}>
                  Inizia una conversazione di test...
                </div>
              )}

              {chatHistory.map((msg, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.message,
                    ...(msg.role === 'user' ? styles.userMessage : styles.aiMessage),
                  }}
                >
                  <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '8px' }}>
                    {msg.role === 'user' ? '👤 User' : '🤖 AI'}
                    {' • '}
                    {msg.timestamp?.toLocaleTimeString()}
                  </div>

                  <div style={{ marginBottom: msg.tokens ? '12px' : 0 }}>{msg.content}</div>

                  {msg.blocked && (
                    <div style={{ ...styles.badge, ...styles.badgeDanger }}>
                      🚫 BLOCKED: {msg.blockedReason}
                    </div>
                  )}

                  {msg.tokens && (
                    <div style={{ fontSize: '11px', opacity: 0.6, marginTop: '8px' }}>
                      🎯 {msg.tokens} tokens • 💰 ${msg.cost?.toFixed(4)} • ⚡ {msg.responseTime}ms
                    </div>
                  )}

                  {msg.error && (
                    <div style={{ ...styles.badge, ...styles.badgeDanger }}>ERROR</div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div style={{ ...styles.message, ...styles.aiMessage }}>
                  <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '8px' }}>🤖 AI</div>
                  <div>Thinking...</div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <input
                type="text"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendTestMessage()}
                placeholder="Scrivi un messaggio di test..."
                style={{ ...styles.input, marginBottom: 0, flex: 1 }}
                disabled={isLoading}
              />
              <button onClick={sendTestMessage} style={styles.button} disabled={isLoading}>
                Invia
              </button>
              <button
                onClick={clearChat}
                style={{ ...styles.button, ...styles.buttonSecondary }}
                disabled={chatHistory.length === 0}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Stats */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>📊 API Stats</div>

            <div style={styles.stat}>
              <span style={styles.statLabel}>Total Tokens</span>
              <span style={styles.statValue}>{stats.totalTokens.toLocaleString()}</span>
            </div>

            <div style={styles.stat}>
              <span style={styles.statLabel}>Total Cost</span>
              <span style={styles.statValue}>${stats.totalCost.toFixed(4)}</span>
            </div>

            <div style={styles.stat}>
              <span style={styles.statLabel}>Conversations</span>
              <span style={styles.statValue}>{stats.conversationCount}</span>
            </div>

            <div style={styles.stat}>
              <span style={styles.statLabel}>Avg Response Time</span>
              <span style={styles.statValue}>{stats.avgResponseTime.toFixed(0)}ms</span>
            </div>

            <div style={styles.stat}>
              <span style={styles.statLabel}>Model</span>
              <span style={styles.statValue}>{stats.model}</span>
            </div>

            <button
              onClick={loadStats}
              style={{ ...styles.button, ...styles.buttonSecondary, width: '100%', marginTop: '12px' }}
            >
              🔄 Refresh Stats
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderRulesTab() {
    return (
      <div>
        <div style={styles.grid}>
          {/* Add Rule */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>➕ Nuova Regola</div>

            <input
              type="text"
              value={newRule.trigger}
              onChange={(e) => setNewRule({ ...newRule, trigger: e.target.value })}
              placeholder="Trigger (es: 'parla di politica')"
              style={styles.input}
            />

            <textarea
              value={newRule.action}
              onChange={(e) => setNewRule({ ...newRule, action: e.target.value })}
              placeholder="Action (es: 'Rispondi: Mi dispiace, non posso parlare di politica')"
              style={styles.textarea}
            />

            <input
              type="text"
              value={newRule.description}
              onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
              placeholder="Descrizione (opzionale)"
              style={styles.input}
            />

            <button onClick={saveRule} style={styles.button}>
              Salva Regola
            </button>
          </div>

          {/* Rules List */}
          <div style={{ ...styles.card, gridColumn: 'span 2' }}>
            <div style={styles.cardTitle}>📋 Regole Attive ({rules.length})</div>

            {rules.length === 0 && (
              <div style={{ textAlign: 'center', color: colors.textSecondary, padding: '32px' }}>
                Nessuna regola configurata
              </div>
            )}

            {rules.map((rule) => (
              <div
                key={rule.id}
                style={{
                  ...styles.stat,
                  display: 'block',
                  marginBottom: '12px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <strong style={{ color: colors.primary }}>🎯 {rule.trigger}</strong>
                  <button
                    onClick={() => deleteRule(rule.id)}
                    style={{
                      ...styles.button,
                      ...styles.buttonDanger,
                      padding: '4px 12px',
                      fontSize: '12px',
                    }}
                  >
                    Elimina
                  </button>
                </div>
                <div style={{ color: colors.textSecondary, fontSize: '13px', marginBottom: '8px' }}>
                  {rule.description}
                </div>
                <div style={styles.codeBlock}>{rule.action}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function renderBlocklistTab() {
    return (
      <div>
        <div style={styles.grid}>
          {/* Add Block */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>🚫 Nuovo Blocco</div>

            <input
              type="text"
              value={newBlock.pattern}
              onChange={(e) => setNewBlock({ ...newBlock, pattern: e.target.value })}
              placeholder="Pattern regex (es: 'medicine|farmaci|diagnosi')"
              style={styles.input}
            />

            <textarea
              value={newBlock.reason}
              onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })}
              placeholder="Motivo blocco (opzionale)"
              style={styles.textarea}
            />

            <button onClick={saveBlock} style={{ ...styles.button, ...styles.buttonDanger }}>
              Aggiungi Blocco
            </button>
          </div>

          {/* Blocklist */}
          <div style={{ ...styles.card, gridColumn: 'span 2' }}>
            <div style={styles.cardTitle}>🚫 Blocklist Attiva ({blocklist.length})</div>

            {blocklist.length === 0 && (
              <div style={{ textAlign: 'center', color: colors.textSecondary, padding: '32px' }}>
                Nessun blocco configurato
              </div>
            )}

            {blocklist.map((block) => (
              <div
                key={block.id}
                style={{
                  ...styles.stat,
                  display: 'block',
                  marginBottom: '12px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <code style={{ color: colors.danger, fontWeight: 'bold' }}>{block.pattern}</code>
                  <button
                    onClick={() => deleteBlock(block.id)}
                    style={{
                      ...styles.button,
                      ...styles.buttonDanger,
                      padding: '4px 12px',
                      fontSize: '12px',
                    }}
                  >
                    Elimina
                  </button>
                </div>
                {block.reason && (
                  <div style={{ color: colors.textSecondary, fontSize: '13px' }}>{block.reason}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function renderSystemTab() {
    return (
      <div>
        <div style={styles.card}>
          <div style={styles.cardTitle}>⚙️ System Prompt</div>

          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            placeholder="System prompt per l'AI..."
            style={{ ...styles.textarea, minHeight: '400px' }}
          />

          <button onClick={saveSystemPrompt} style={{ ...styles.button, marginTop: '12px' }}>
            Salva System Prompt
          </button>
        </div>
      </div>
    );
  }

  function renderLogsTab() {
    return (
      <div>
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={styles.cardTitle} style={{ marginBottom: 0 }}>
              📜 Conversation Logs ({logs.length})
            </div>
            <button onClick={loadLogs} style={{ ...styles.button, ...styles.buttonSecondary }}>
              🔄 Refresh
            </button>
          </div>

          {logs.length === 0 && (
            <div style={{ textAlign: 'center', color: colors.textSecondary, padding: '32px' }}>
              Nessun log disponibile
            </div>
          )}

          {logs.map((log, i) => (
            <div key={i} style={{ ...styles.stat, display: 'block', marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{new Date(log.timestamp).toLocaleString()} • {log.assistedName || 'Unknown'}</span>
                {log.userType === 'caregiver' && (
                  <div style={{ ...styles.badge, background: '#00ff88', color: '#1a1a1a', fontSize: '10px', padding: '2px 8px' }}>
                    CAREGIVER
                  </div>
                )}
                {log.userType === 'donata' && (
                  <div style={{ ...styles.badge, background: '#0099ff', color: '#ffffff', fontSize: '10px', padding: '2px 8px' }}>
                    DONATA
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '8px' }}>
                <div style={{ ...styles.badge, background: colors.primary, color: '#ffffff' }}>
                  👤 User
                </div>
                <span>{log.userMessage}</span>
              </div>

              <div style={{ marginBottom: '8px' }}>
                <div style={{ ...styles.badge, background: colors.surface, border: `1px solid ${colors.border}` }}>
                  🤖 AI
                </div>
                <span>{log.aiResponse}</span>
              </div>

              {log.blocked && (
                <div style={{ ...styles.badge, ...styles.badgeDanger }}>🚫 BLOCKED: {log.blockedReason}</div>
              )}

              <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '8px' }}>
                🎯 {log.tokens} tokens • 💰 ${log.cost?.toFixed(4)} • ⚡ {log.responseTime}ms
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div className="page-header blue">
        <h1>AI Training Studio</h1>
        <p className="subtitle">Addestra, monitora e ottimizza l'AI di DonatApp</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: `1px solid ${colors.border}` }}>
        {[
          { id: 'test', label: '💬 Test' },
          { id: 'rules', label: '📋 Rules' },
          { id: 'blocklist', label: '🚫 Blocklist' },
          { id: 'system', label: '⚙️ System' },
          { id: 'logs', label: '📜 Logs' },
          // { id: 'voice', label: '🎙️ Voice' },  // ❌ Rimosso: causava errori 401
          // { id: 'voicechat', label: '🗣️ Voice Chat' },  // ❌ Rimosso: causava errori 401
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.button,
              ...(activeTab === tab.id ? {} : styles.buttonSecondary),
              borderRadius: '4px 4px 0 0',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'test' && renderTestTab()}
      {activeTab === 'rules' && renderRulesTab()}
      {activeTab === 'blocklist' && renderBlocklistTab()}
      {activeTab === 'system' && renderSystemTab()}
      {activeTab === 'logs' && renderLogsTab()}
      {/* {activeTab === 'voice' && <VoiceAnalyticsTab />} */}
      {/* {activeTab === 'voicechat' && <VoiceChatTab />} */}
    </div>
  );
}
